<script lang="ts" setup>
import AccordionPanel from "primevue/accordionpanel";
import AccordionContent from "primevue/accordioncontent";
import AccordionHeader from "primevue/accordionheader";
import {computed} from "vue";
import dayjs from "dayjs";

import 'dayjs/locale/ru';
import {price} from "@/filters/price";

dayjs.locale('ru')

type TOrderStatus = 'accepted' | 'done' | 'inProgress'
type TOrderShippingMethod = 'pickup' | 'delivery'

export type TOrder = {
  id: number,
  dateCreated: string,
  orderStatus: TOrderStatus,
  requisites: string,
  shippingMethod: TOrderShippingMethod,
  orderGoods: TOrderItem[],
  total: string
}

export type TOrderItem = {
  id: number,
  title: string,
  fields: {
    sku: string,
    count: number,
    price: number,
    comment: string
  }
}

const props = defineProps<{ order: TOrder, accValue: number }>()

// computed
const computedDate = computed(() => {
  return dayjs(props.order.dateCreated).format('DD MMMM YYYY')
})

const shoppingMethod = computed(() => {
  if (props.order.shippingMethod === 'pickup') {
    return 'самовывоз'
  }

  return 'достака'
})

const orderStatus = computed(() => {
  if (props.order.orderStatus === 'accepted') {
    return 'принят'
  }

  if (props.order.orderStatus === 'inProgress') {
    return 'в прогрессе'
  }

  return 'завершен'
})

const isShownPriceColumn = computed<boolean>(() => {
  return Object.values(props.order.orderGoods).some((item: TOrderItem) => item.fields.price)
})


</script>

<template>
  <AccordionPanel :data-id="'order-'+props.order.id" :value="props.accValue">
    <div class="px-2.5 desktop:px-10 text-base desktop:text-[20px] leading-[18px] py-2.5">{{ computedDate }}</div>
    <AccordionHeader
      class="w-full flex flex-row p-2.5 desktop:px-10 desktop:py-5 bg-[#F7F7F7] border-t border-x border-transparent transition-colors items-center">
      <div class="flex flex-col desktop:grid grid-cols-12 w-full max-desktop:text-base">
        <div class="col-span-5 text-left">Номер заказа: {{ props.order.id }}</div>
        <div class="col-span-3 text-left">Тип доставки: <span
          class="text-[hsl(var(--roke-text-blue))]">{{ shoppingMethod }}</span></div>
        <div class="col-span-3 text-left">Статус заказа: <span class="text-[hsl(var(--roke-text-blue))]">{{
            orderStatus
          }}</span></div>
        <div class="col-span-2 text-right"></div>
      </div>
    </AccordionHeader>
    <AccordionContent>
      <div class="bg-[#F7F7F7] desktop:border-b desktop:border-x px-2.5 desktop:px-5 desktop:border-[#D9D9D9]">
        <table class="desktop:bg-white w-full desktop:border desktop:border-[#D9D9D9]">
          <thead class="max-desktop:hidden">
          <tr class="">
            <th class="border border-[#D9D9D9] px-5 py-[18px] text-[18px] leading-[16.2px] font-semibold text-left">
              Наименование товара
            </th>
            <th class="border border-[#D9D9D9] px-5 py-[18px] text-[18px] leading-[16.2px] font-semibold text-left">
              Описание
            </th>
            <th class="border border-[#D9D9D9] px-5 py-[18px] text-[18px] leading-[16.2px] font-semibold text-center">
              Количество
            </th>
            <th class="border border-[#D9D9D9] px-5 py-[18px] text-[18px] leading-[16.2px] font-semibold text-center">
              Комментарий
            </th>
            <th v-if="isShownPriceColumn"
                class="border border-[#D9D9D9] px-5 py-[18px] text-[18px] leading-[16.2px] font-semibold text-center">
              Сумма
            </th>
          </tr>
          </thead>
          <tbody class="max-desktop:flex flex-col gap-2.5">
          <tr v-for="item in props.order.orderGoods" :key="item.id"
              class="max-desktop:flex flex-row max-desktop:bg-white max-desktop:border border-[#D9D9D9] flex-wrap">
            <td
              class="desktop:border border-[#D9D9D9] max-desktop:p-2.5 px-5 py-9 text-[24px] leading-none max-desktop:w-full">
              {{ item.title }}
            </td>
            <td
              class="desktop:border border-[#D9D9D9] max-desktop:p-2.5 px-5 py-9 text-[24px] leading-none max-desktop:w-full max-desktop:-order-1">
              <span class="text-base leading-none desktop:text-[18px] desktop:leading-[16.2px] desktop:opacity-40">Артикул:</span>
              <span class="text-base desktop:text-[20px] leading-none ml-2 max-desktop:font-semibold">{{
                  item.fields.sku
                }}</span>
            </td>
            <td
              class="max-desktop:mt-4 desktop:border border-[#D9D9D9] max-desktop:p-2.5 px-5 py-9 text-base leading-none desktop:text-[20px] desktop:leading-[18px] desktop:font-semibold desktop:text-center max-desktop:w-1/2">
              <span class="desktop:hidden">Количество: </span><span
              class="max-desktop:font-semibold">{{ item.fields.count }}</span>
            </td>
            <td
              class="max-desktop:mt-4 desktop:border border-[#D9D9D9] max-desktop:p-2.5 px-5 py-9 text-base leading-none desktop:text-[20px] desktop:leading-[18px] desktop:text-center max-desktop:w-1/2">
              <span class="desktop:hidden">Комментарий: </span><span
              class="max-desktop:font-semibold">{{ item.fields.comment }}</span>
            </td>
            <td
              v-if="item.fields.price"
              class="max-desktop:mt-4 desktop:border border-[#D9D9D9] max-desktop:p-2.5 px-5 py-9 text-base leading-none desktop:text-[24px] desktop:text-center max-desktop:w-1/2">
              <span>Сумма: </span><span class="max-desktop:font-semibold">{{ price(item.fields.price / 100) }}</span>
            </td>
          </tr>
          </tbody>
        </table>
        <div class="flex flex-col desktop:flex-row justify-between py-5 max-desktop:gap-4">
          <div class="text-[18px] desktop:text-[24px] leading-none max-desktop:flex justify-between">
            <span class="desktop:hidden">Товаров на сумму: </span>
            <span>{{ price(+props.order.total / 100) }}</span>
          </div>
          <div class="text-[24px] desktop:text-[38px] leading-none font-semibold max-desktop:flex justify-between">
            <span class="desktop:hidden">Итого: </span>
            <span>{{ price(+props.order.total / 100) }}</span>
          </div>
        </div>
      </div>
    </AccordionContent>
  </AccordionPanel>
</template>

<style lang="scss" scoped>
[data-pc-name="accordionheader"] {
  &[data-p-active="true"] {
    @apply desktop:border-[#D9D9D9];
  }
}
</style>
